/**
 * Analyzer: Setup Combo Calibration (Wave G.1, v7.4.5)
 *
 * Companion to setupPatternCalibration. Where that analyzer asks
 * "do my Wolf Stack entries outperform baseline?" — this one asks
 * "what happens when TWO patterns stack on the same entry?"
 *
 * For every PAIR of pattern types (X, Y):
 *   1. Find trades where BOTH X and Y fired together at entry
 *   2. Compare WR + avg-R against the trader's overall baseline
 *      (all trades in the same window)
 *   3. Surface combos that meaningfully outperform OR underperform
 *
 * The trader's answer to "should I size up when patterns stack?" lives
 * here. If Wolf Stack alone is 60% but Wolf Stack + Holy Grail is 78%,
 * the stacked signal is real and the analyzer says so.
 *
 * Sample-size guards (intentionally lower than the single-pattern
 * analyzer — pair combos are rarer):
 *   - At least 6 trades with BOTH patterns
 *   - WR gap ≥ 12% vs baseline (looser than the 15% single threshold)
 *
 * SUCCESS when combo outperforms.
 * INFO when combo UNDERperforms — softer than the single-pattern
 * underperform warning because the rarer sample is more fragile.
 *
 * Window: rolling 30 trading days.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';
import { PATTERN_CATALOG } from '../../../barAnalysis/setupPatterns.js';

export const key = 'setupComboCalibration';
export const tier = 'free';

const MIN_COMBO_SAMPLE = 6;
const MIN_BASELINE_SAMPLE = 10;
const MIN_WR_GAP = 0.12;

// Max number of observations to emit per run — combos can be combinatorial.
// We rank by absolute WR gap descending and keep the top N.
const MAX_OBSERVATIONS = 3;

const inWindow = (trade, fromDateStr, toDateStr) => {
  if (!trade?.date || typeof trade.date !== 'string') return false;
  return trade.date >= fromDateStr && trade.date <= toDateStr;
};

const isWinner = (trade) =>
  Number.isFinite(parseFloat(trade?.pnl)) && parseFloat(trade.pnl) > 0;

const summarize = (trades) => {
  if (trades.length === 0) return { count: 0, wr: 0, avgR: 0 };
  const wins = trades.filter(isWinner).length;
  const rs = trades.map((t) => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR =
    rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : 0;
  return { count: trades.length, wr: wins / trades.length, avgR };
};

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) =>
  Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

const tradeHasPattern = (trade, type) => {
  const patterns = trade?.barAnalysis?.setupPatterns;
  if (!Array.isArray(patterns)) return false;
  return patterns.some((p) => p?.type === type);
};

const labelOf = (type) => PATTERN_CATALOG[type]?.label || type;

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 30 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);
  const windowed = allTrades.filter((t) => inWindow(t, fromDateStr, anchor));
  if (windowed.length < MIN_BASELINE_SAMPLE) return null;

  // Baseline: all trades in window. This is the "did pattern stacking actually
  // beat my normal trading?" reference point — not "with-pattern vs without-
  // pattern" like the single-pattern analyzer.
  const baseline = summarize(windowed);
  if (baseline.count < MIN_BASELINE_SAMPLE) return null;

  const patternTypes = Object.keys(PATTERN_CATALOG);

  // For each (X, Y) pair where X < Y (alphabetical, to avoid (X,Y) AND (Y,X)),
  // compute the combo cohort.
  const combos = [];
  for (let i = 0; i < patternTypes.length; i++) {
    for (let j = i + 1; j < patternTypes.length; j++) {
      const a = patternTypes[i];
      const b = patternTypes[j];
      const both = windowed.filter(
        (t) => tradeHasPattern(t, a) && tradeHasPattern(t, b)
      );
      if (both.length < MIN_COMBO_SAMPLE) continue;

      const comboStats = summarize(both);
      const gap = comboStats.wr - baseline.wr;
      if (Math.abs(gap) < MIN_WR_GAP) continue;

      combos.push({ a, b, comboStats, gap });
    }
  }

  if (combos.length === 0) return null;

  // Rank by absolute WR gap so the strongest stacked-edge signals surface first.
  combos.sort((x, y) => Math.abs(y.gap) - Math.abs(x.gap));
  const top = combos.slice(0, MAX_OBSERVATIONS);

  const observations = top.map(({ a, b, comboStats, gap }) => {
    const aLabel = labelOf(a);
    const bLabel = labelOf(b);
    const comboName = `${aLabel} + ${bLabel}`;

    if (gap > 0) {
      return makeObservation({
        id: `${key}:${anchor}:${a}+${b}:outperforms`,
        analyzer: key,
        scope: 'progress',
        scopeId: anchor,
        severity: SEVERITY.SUCCESS,
        category: CATEGORY.PROCESS,
        title: `${comboName} together outperforms baseline by ${fmtPct(Math.abs(gap))}`,
        detail: `${comboStats.count} trades fired BOTH ${aLabel} and ${bLabel} at entry → ${fmtPct(comboStats.wr)} WR (avg ${fmtR(comboStats.avgR)}). Your ${windowDays}D baseline across all ${baseline.count} trades is ${fmtPct(baseline.wr)} WR (avg ${fmtR(baseline.avgR)}). The stacked signal is doing real work for you.`,
        action: `When you see ${aLabel} AND ${bLabel} line up on the same entry, lean in — that's a confluence the data says you can trust.`,
        evidence: {
          combo: { types: [a, b], stats: comboStats },
          baseline,
          gap,
          windowDays,
        },
        confidence: 0.82,
        priority: 76,
      });
    }

    return makeObservation({
      id: `${key}:${anchor}:${a}+${b}:underperforms`,
      analyzer: key,
      scope: 'progress',
      scopeId: anchor,
      severity: SEVERITY.INFO,
      category: CATEGORY.PROCESS,
      title: `${comboName} together UNDERperforms baseline by ${fmtPct(Math.abs(gap))}`,
      detail: `${comboStats.count} trades fired BOTH ${aLabel} and ${bLabel} at entry → ${fmtPct(comboStats.wr)} WR (avg ${fmtR(comboStats.avgR)}). Your ${windowDays}D baseline across all ${baseline.count} trades is ${fmtPct(baseline.wr)} WR (avg ${fmtR(baseline.avgR)}). Sample is small — could be noise — but worth watching.`,
      action: `When ${aLabel} and ${bLabel} both fire, audit what's different about those entries vs your usual setups. Could be timing, could be size, could be a pattern-quality issue.`,
      evidence: {
        combo: { types: [a, b], stats: comboStats },
        baseline,
        gap,
        windowDays,
      },
      confidence: 0.65,
      priority: 64,
    });
  });

  return observations.length > 0 ? observations : null;
};
